import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { Feedstock } from '../../../models/project/feedstock/feedstock.model';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import Utils from '../../../util';
import { FeedstockBreakdownDataItem } from '../../../models/data-structures/results/feedstock-breakdown/feedstock-breakdown-data-item.model';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import FeedstockUtil from '../../../project/feedstock/feedstock.util';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';

@Injectable({
  providedIn: 'root'
})
/**
 * A service that contains functionality for creating the feedstock breakdown data.
 */
export class FeedstockBreakdownService implements OnDestroy {
  // notifiers

  /**
   * A notifier that is true when the service is ready to be used.
   */
  serviceReadyNotifier = new BehaviorSubject(false);
  /**
   * A notifier that is emitted when the service is destroyed; used to clean up subscriptions created in the class.
   */
  private onDestroyNotifier = new EventEmitter();

  // private properties

  /**
   * A unit for the metric version of the data; ton/day.
   */
  private metricUnit: Unit | null = null;
  /**
   * A unit for the imperial version of the data; short ton/day.
   */
  private imperialUnit: Unit | null = null;

  // computational properties

  /**
   * An array of the current feedstocks in the project.
   */
  private feedstocks?: Feedstock[];
  /**
   * The value of the total mass of all the current feedstocks in ton/day
   */
  private totalMassInTonPerDay: number = 0;
  /**
   * The data array of feedstock breakdown data.
   */
  private feedstockBreakdownData?: FeedstockBreakdownDataItem[];

  // boolean notifier states

  /**
   * Boolean that determines if the feedstock service is ready.
   */
  private feedstockServiceReady = false;
  /**
   * Boolean that determines if the project communication service is ready.
   */
  private projCommServiceReady = false;
  /**
   * Boolean that determines if the unit service is ready.
   */
  private unitServiceReady = false;


  /**
   * Constructor; Injects the FeedstockService, UnitService, ProjectCommunicationService.
   * the mass of each feedstock.
   * @param unitService is the UnitService; Used to fetch and convert units.
   * @param projectCommService is the ProjectCommunicationService; Used to get the precision set by the user.
   * @param toastrService is the ConfiguredToastrService; Used to relay messages to the user.
   * @param translationService
   */
  constructor(private unitService: UnitService,
              private projectCommService: ProjectCommunicationService,
              private toastrService: ConfiguredToastrService,
              private translationService: AnessaTranslationService
  ) {
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.feedstockServiceReady = loaded;
        if (loaded) {
          const project = this.projectCommService.getResultProjectState();
          this.feedstocks = project?.feedstocks;
          this.compute();
        }
        this.updateNotifier();
      });
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        this.projCommServiceReady = loaded;
        this.updateNotifier();
      });
    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((loaded: boolean) => {
        this.unitServiceReady = loaded;
        if (loaded) {
          this.metricUnit = this.unitService.getUnitById('000000000000000000004901');
          this.imperialUnit = this.unitService.getUnitById('000000000000000000004902');
        }
        this.updateNotifier();
      });
  }

  /**
   * Emits, and unsubscribes, from the onDestroyNotifier.
   */
  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * Get the share of the provided feedstock, as a percentage.
   * @param feedstock is the feedstock for which the share will be calculated.
   */
  getFeedstockShare(feedstock: Feedstock): number | null {
    if (feedstock && feedstock.quantities) {
      const massInTonPerDay = FeedstockUtil.getNormalizedMassPerDay(new Feedstock(feedstock), this.unitService);
      const share = massInTonPerDay / this.totalMassInTonPerDay;
      return Utils.roundToPrecision(share * 100, this.projectCommService.getPrecision());
    }
    return null;
  }

  /**
   * Get the feedstock breakdown data.
   */
  getFeedstockBreakdownData(): FeedstockBreakdownDataItem[] | undefined {
    return this.feedstockBreakdownData;
  }

  /**
   * Get a FeedstockBreakdownDataItem for the total row of the breakdown table.
   */
  getFeedstockBreakdownTotal(): FeedstockBreakdownDataItem | null {
    if (this.feedstocks && this.feedstocks.length) {
      return new FeedstockBreakdownDataItem(
        this.translationService.get('RESULTS.GENERAL.TOTAL'),
        Utils.addCommas(this.totalMassInTonPerDay),
        this.translationService.get('DATABASE.UNITS.AMOUNT_PER_PERIOD.TON_DAY'),
        Utils.roundToPrecisionFixed(100, this.getProjectDecimalPrecision()) + '%'
      );
    }
    return null;
  }


  /**
   * Get the feedstock breakdown data with imperial values.
   */
  convertFeedstockBreakdownDataToImperial(data: FeedstockBreakdownDataItem[]): FeedstockBreakdownDataItem[] {
    if (!this.feedstockBreakdownData) {
      throw Error(this.translationService.get('RESULTS.ERRORS.NO_IMPERIAL_FEEDSTOCK_BREAKDOWN_DATA'));
    }
    const convertedData: FeedstockBreakdownDataItem[] = Utils.deepCopy(data);
    for (const item of convertedData) {
      const numericValue = Utils.removeCommas(item.amount);
      const convertedValue = this.metricUnit.convertToUnit(numericValue, this.imperialUnit);
      item.amount = Utils.addCommas(convertedValue);
      item.unit = this.translationService.get('DATABASE.UNITS.AMOUNT_PER_PERIOD.SHORT_TON_DAY');
    }
    return convertedData;
  }

  getProjectDecimalPrecision(): number | null {
    return this.projectCommService.getPrecision();
  }

  /**
   * Check all the service ready properties and update the feedstock breakdown service notifier accordingly.
   */
  private updateNotifier(): void {
    this.serviceReadyNotifier.next(this.feedstockServiceReady && this.projCommServiceReady && this.unitServiceReady);
  }

  /**
   * Computes data that will be provided by the service.
   */
  private compute(): void {
    this.calculateTotalMassInTonsPerDay();
    this.setFeedstockBreakdownData();
  }

  /**
   * Calculate, and set, the total mass, in ton/day, of all feedstocks
   */
  private calculateTotalMassInTonsPerDay(): void {
    if (!this.feedstocks) return;
    this.totalMassInTonPerDay = 0;
    for (const feedstock of this.feedstocks) {
      this.totalMassInTonPerDay += FeedstockUtil.getNormalizedMassPerDay(new Feedstock(feedstock), this.unitService);
    }
  }

  /**
   * Create, and set, the feedstock breakdown data.
   */
  private setFeedstockBreakdownData() {
    this.feedstockBreakdownData = [];
    if (!this.feedstocks) {
      this.toastrService.error(this.translationService.get('RESULTS.ERRORS.UNABLE_TO_RETRIEVE_FEEDSTOCKS'));
      return;
    }
    if (this.feedstocks.length < 1) {
      this.toastrService.warning(
        this.translationService.get('RESULTS.ERRORS.ONE_FEEDSTOCK_REQUIRED'),
        this.translationService.get('RESULTS.ERRORS.NO_FEEDSTOCKS_FOUND')
      );
    }
    const precision = this.getProjectDecimalPrecision();
    for (const feedstock of this.feedstocks) {
      const name = feedstock.name;
      const amount = Utils.addCommas(FeedstockUtil.getNormalizedMassPerDay(new Feedstock(feedstock), this.unitService));
      const unit = this.translationService.get('DATABASE.UNITS.AMOUNT_PER_PERIOD.TON_DAY');
      const share = this.getFeedstockShare(feedstock);
      if (!share) continue;
      const roundedShare = Utils.roundToPrecisionFixed(share, precision) + '%';
      this.feedstockBreakdownData.push(new FeedstockBreakdownDataItem(name, amount, unit, roundedShare));
    }
  }
}
